import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Clock, CheckCircle } from 'lucide-react';
import { format, startOfWeek, endOfWeek } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Timesheets() {
  const [startDate, setStartDate] = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [cleanerId, setCleanerId] = useState('');
  const [cleaners, setCleaners] = useState([]);
  const [timesheets, setTimesheets] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCleaners = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users/cleaners`);
        setCleaners(response.data.filter(c => c.is_active));
      } catch (error) {
        console.error('Failed to fetch cleaners');
      }
    };
    fetchCleaners();
  }, []);

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/timesheets?start_date=${startDate}&end_date=${endDate}`;
      if (cleanerId) {
        url += `&cleaner_id=${cleanerId}`;
      }
      const response = await axios.get(url);
      setTimesheets(response.data);
    } catch (error) {
      toast.error('Failed to fetch timesheets');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      let url = `${API_URL}/api/timesheets/export?start_date=${startDate}&end_date=${endDate}`;
      if (cleanerId) {
        url += `&cleaner_id=${cleanerId}`;
      }
      const response = await axios.get(url, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `timesheets_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Timesheets exported');
    } catch (error) {
      toast.error('Failed to export timesheets');
    }
  };

  const setCurrentWeek = () => {
    setStartDate(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
    setEndDate(format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="timesheets-page">
        <div className="bg-white border border-slate-200 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
              />
            </div>
            <div>
              <Label htmlFor="cleaner_filter">Cleaner</Label>
              <Select value={cleanerId || 'all'} onValueChange={(value) => setCleanerId(value === 'all' ? '' : value)}>
                <SelectTrigger data-testid="cleaner-filter">
                  <SelectValue placeholder="All cleaners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cleaners</SelectItem>
                  {cleaners.map((cleaner) => (
                    <SelectItem key={cleaner.id} value={cleaner.id}>
                      {cleaner.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={setCurrentWeek}
                variant="outline"
                className="flex-1"
                data-testid="current-week-button"
              >
                Current Week
              </Button>
              <Button
                onClick={fetchTimesheets}
                disabled={loading}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                data-testid="fetch-timesheets-button"
              >
                {loading ? 'Loading...' : 'Fetch'}
              </Button>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleExport}
                disabled={!timesheets}
                variant="outline"
                className="w-full"
                data-testid="export-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {timesheets && (
          <div className="bg-white border border-slate-200 overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Cleaner
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Scheduled
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Actual
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Approved/Paid
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Variance
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Visits
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {timesheets.entries.map((entry) => {
                  const isOvertime = entry.variance_minutes > 0;
                  const isUndertime = entry.variance_minutes < 0;
                  const hasApprovalDiff = entry.paid_minutes !== entry.actual_minutes;
                  
                  return (
                    <tr key={entry.cleaner_id} data-testid={`timesheet-row-${entry.cleaner_id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                        {entry.cleaner_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-slate-900">{entry.scheduled_hours}h</div>
                        <div className="text-xs text-slate-500">{entry.scheduled_minutes}min</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="text-sm font-medium text-slate-900">{entry.actual_hours}h</div>
                            <div className="text-xs text-slate-500">{entry.actual_minutes}min</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`flex items-center justify-end gap-2 ${hasApprovalDiff ? 'text-emerald-700' : 'text-slate-900'}`}>
                          {hasApprovalDiff && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                          <div>
                            <div className="text-sm font-semibold">{entry.paid_hours}h</div>
                            <div className="text-xs text-slate-500">{entry.paid_minutes}min</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`inline-flex items-center gap-1 text-sm font-semibold ${
                          isOvertime ? 'text-amber-700' : isUndertime ? 'text-blue-700' : 'text-slate-600'
                        }`}>
                          {isOvertime && <span>+</span>}
                          {(entry.variance_minutes / 60).toFixed(1)}h
                        </div>
                        <div className={`text-xs ${
                          isOvertime ? 'text-amber-600' : isUndertime ? 'text-blue-600' : 'text-slate-500'
                        }`}>
                          ({entry.variance_percentage > 0 ? '+' : ''}{entry.variance_percentage}%)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-slate-600">
                        {entry.visit_count}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {timesheets.entries.length > 0 && (
                <tfoot className="bg-slate-50">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900">
                      TOTAL
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-slate-900">
                        {timesheets.entries.reduce((sum, e) => sum + e.scheduled_hours, 0).toFixed(2)}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-slate-900">
                        {timesheets.entries.reduce((sum, e) => sum + e.actual_hours, 0).toFixed(2)}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-emerald-700">
                        {timesheets.entries.reduce((sum, e) => sum + e.paid_hours, 0).toFixed(2)}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-slate-900">
                        {(timesheets.entries.reduce((sum, e) => sum + e.variance_minutes, 0) / 60).toFixed(1)}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-slate-600">
                      {timesheets.entries.reduce((sum, e) => sum + e.visit_count, 0)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
